import {
  ADMIN_BUILD_CHECK_MIN_INTERVAL_MS,
  ADMIN_BUILD_REFRESH_TOAST_ID,
  createAdminBuildUpdateChecker,
} from '@/lib/admin-build'
import { showActionRequiredToast } from '@/components/ui/toast'
import { useEffect, useState } from 'react'

function refreshPage(): void {
  window.location.reload()
}

export function showAdminBuildUpdateToast(): void {
  showActionRequiredToast("We've made some updates.", {
    id: ADMIN_BUILD_REFRESH_TOAST_ID,
    description: 'Refresh to see the latest changes.',
    duration: Infinity,
    action: {
      label: 'Refresh',
      onClick: refreshPage,
    },
  })
}

type AdminBuildCheckerOptions = NonNullable<Parameters<typeof createAdminBuildUpdateChecker>[0]>

export type AdminBuildUpdateToastProps = Omit<AdminBuildCheckerOptions, 'onUpdateAvailable'> & {
  routeHref?: string
  pollIntervalMs?: number
}

export function AdminBuildUpdateToast({
  routeHref,
  currentBuildId,
  fetchLatest,
  minIntervalMs = ADMIN_BUILD_CHECK_MIN_INTERVAL_MS,
  pollIntervalMs = minIntervalMs,
  now,
}: AdminBuildUpdateToastProps = {}) {
  const [checker] = useState(() =>
    createAdminBuildUpdateChecker({
      currentBuildId,
      fetchLatest,
      minIntervalMs,
      now,
      onUpdateAvailable: showAdminBuildUpdateToast,
    })
  )

  useEffect(() => {
    void checker.check({ force: true })

    function checkForUpdate() {
      void checker.check()
    }

    function checkWhenVisible() {
      if (document.visibilityState !== 'hidden') {
        checkForUpdate()
      }
    }

    window.addEventListener('focus', checkForUpdate)
    window.addEventListener('online', checkForUpdate)
    document.addEventListener('visibilitychange', checkWhenVisible)
    const pollIntervalId =
      pollIntervalMs > 0 ? window.setInterval(checkWhenVisible, pollIntervalMs) : null

    return () => {
      window.removeEventListener('focus', checkForUpdate)
      window.removeEventListener('online', checkForUpdate)
      document.removeEventListener('visibilitychange', checkWhenVisible)
      if (pollIntervalId != null) {
        window.clearInterval(pollIntervalId)
      }
    }
  }, [checker, pollIntervalMs])

  useEffect(() => {
    void checker.check()
  }, [checker, routeHref])

  return null
}
